import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Web3Provider, TransactionRequest } from '@ethersproject/providers';
import { BigNumber, BigNumberish } from '@ethersproject/bignumber';
import { helpers, Network } from '@kattana-io/common';
import { buildSwapTransaction } from '../helpers/buildTransaction';
import { ICoin } from '../reducers/tradingReducer';
import { actionAppendTransaction } from '../../../actions/transactions';
import { toIntermediateTransaction } from '../../../helpers/intermediateTransactions';
const { toChecksumAddress } = require('ethereum-checksum-address');

enum swapTexts {
  swap = 'Swap',
  allowance = 'Asking allowance',
  allowanceMining = 'Mining allowance',
  txAsking = 'Asking transaction',
  txMining = 'Mining transaction',
}

/**
 * Returns the gas value plus a margin for unexpected or variable gas costs
 * @param value the gas value to pad
 */
const calculateGasMargin = (value: BigNumber): BigNumber =>
  value.mul(120).div(100);

const BASE_TOKEN = '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';
const MaxUint256 =
  '115792089237316195423570985008687907853269984665640564039457584007913129639935';
/**
 * Check if provider from data has enough allowance
 */
const AskAllowance = async (
  userAddress: string,
  provider: any,
  base: ICoin,
  priceRoute: any,
  network: number,
  setAllowanceMining: any,
  dispatch: any
): Promise<any> => {
  if (toChecksumAddress(base.address) === toChecksumAddress(BASE_TOKEN)) {
    // We can't use allowance for base token like ETH, BNB
    return false;
  }
  const { tokenTransferProxy } = priceRoute;

  const tokenContract = new ethers.Contract(
    base.address,
    helpers.ERC20Abi,
    provider
  );
  /**
   * So there is two type of tokens
   * one restricts max approval
   * seconds doesnt' care
   */
  let useExact = false;
  const estimatedGas = await tokenContract.estimateGas
    .approve(tokenTransferProxy, MaxUint256)
    .catch(() => {
      useExact = true; // general fallback for tokens which restrict approval amounts
      console.log('approval: switch to using exact amount');
      return tokenContract.estimateGas.approve(
        tokenTransferProxy,
        priceRoute.srcAmount
      );
    });

  const transaction = await tokenContract.approve(
    tokenTransferProxy,
    useExact ? priceRoute.srcAmount : MaxUint256,
    {
      gasLimit: calculateGasMargin(estimatedGas),
    }
  );
  console.log('approval transacrion: ', transaction);

  dispatch(
    actionAppendTransaction(
      toIntermediateTransaction(transaction.hash, network)
    )
  );
  setAllowanceMining(true);
  await transaction.wait();
};

/**
 * Check if provider from data has enough allowance
 */
const AllowanceRequired = async (
  userAddress: string,
  provider: any,
  base: ICoin,
  priceRoute: any
): Promise<boolean> => {
  if (toChecksumAddress(base.address) === toChecksumAddress(BASE_TOKEN)) {
    // We can't use allowance for base token like ETH, BNB
    return false;
  }
  const { tokenTransferProxy, srcAmount } = priceRoute;
  const tokenContract = new ethers.Contract(
    base.address,
    helpers.ERC20Abi,
    provider
  );
  const responseInBigNum = await tokenContract.allowance(
    userAddress,
    tokenTransferProxy
  );
  /**
   * If allowed 0 than we can skip other checks
   */
  if (responseInBigNum.toString() === '0') {
    return true;
  }

  const available = BigNumber.from(responseInBigNum);
  const required = BigNumber.from(srcAmount);

  console.log(
    base.address,
    ' allowed: ',
    available.toString(),
    ' required: ',
    required.toString()
  );
  /**
   * if required > available => approval required
   * required < available => approval NOT required
   */
  return required.gt(available);
};

/**
 * Actual trade itself
 * @returns
 */
const useMarketTrade = ({
  base,
  quote,
  network,
  slippage,
  userAddress,
  priceRoute,
  onNotify,
  onWalletRefresh,
}: any) => {
  const dispatch = useDispatch();
  const injectedEth = (window as any).ethereum;
  const provider = injectedEth
    ? new Web3Provider((window as any).ethereum)
    : null;
  const signer = provider ? provider.getSigner() : null;

  const [lock, setLock] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [swapText, setSwapText] = useState<string>(swapTexts.swap);
  const [allowanceMining, setAllowanceMining] = useState<boolean>(false);

  useEffect(() => {
    if (allowanceMining) {
      setSwapText(swapTexts.allowanceMining);
    }
  }, [allowanceMining]);

  /**
   * Refetch wallet balances after swap completed
   */
  const resetState = () => {
    setLoading(false);
    setLock(false);
    setSwapText(swapTexts.swap);
    onWalletRefresh([base.address, quote.address]);
  };

  const checkAllowance = async (): Promise<{
    required: boolean;
    error: boolean;
  }> => {
    let error = false;
    const required = await AllowanceRequired(
      userAddress,
      provider,
      base,
      priceRoute
    );
    if (required) {
      onNotify({ message: 'Not enough allowance' });
      try {
        await AskAllowance(
          userAddress,
          signer,
          base,
          priceRoute,
          network,
          setAllowanceMining,
          dispatch
        );
        setAllowanceMining(false);
      } catch (e: any) {
        console.warn('Transaction failed: ' + e.message);
        if (e.reason) {
          onNotify({ kind: 'warning', message: e.reason });
        } else {
          onNotify({ kind: 'warning', message: 'transaction failed' });
        }
        setAllowanceMining(false);
        error = true;
      }
    }

    return {
      required,
      error,
    };
  };

  /**
   * Actual swap code
   */
  const onSwap = async () => {
    if (!provider || !signer || lock) {
      return;
    }
    setLock(true);

    setSwapText(swapTexts.allowance);
    const allowance = await checkAllowance();
    if (allowance.error) {
      resetState();
      return;
    }

    setSwapText(swapTexts.txAsking);
    const { data, error } = await buildSwapTransaction({
      base,
      quote,
      network,
      slippage,
      userAddress,
      priceRoute,
    });
    if (error) {
      onNotify({ kind: 'warning', message: error });
      setLock(false);
      setSwapText(swapTexts.swap);
      return;
    } else {
      try {
        const hexGasPrice = ethers.utils.hexValue(Number(data.gasPrice));
        const hexGas = ethers.utils.hexValue(Number(data.gas));

        const tx: TransactionRequest = {
          from: data.from,
          chainId: data.chainId,
          gasLimit: hexGas,
          gasPrice: hexGasPrice,
          to: data.to,
          data: data.data,
          value: data.value, // string fits type bignumberish, number will cause crash on native coin
        };
        const transaction = await signer.sendTransaction(tx);
        dispatch(
          actionAppendTransaction(
            toIntermediateTransaction(transaction.hash, network, true)
          )
        );
        console.log('Raw transaction: ', transaction);
        onNotify({ message: 'Transaction submitted, waiting confirmations' });
        setSwapText(swapTexts.txMining);
        const receipt = await transaction.wait();
        console.log('Transaction receipt: ', receipt);
        onNotify({ message: 'Swap completed' });
        resetState();
      } catch (err) {
        console.error('Error', err);
        onNotify({ kind: 'warning', message: 'Could not swap, error occured' });
        resetState();
      }
    }
  };

  return {
    loading,
    onSwap,
    swapText,
  };
};

export default useMarketTrade;
